import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Target, Crosshair } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Board({ title, icon: Icon, rows, field, testid, emptyText }) {
  return (
    <div className="border border-[#27272A] bg-[#121214]" data-testid={testid}>
      <div className="flex items-center gap-3 px-8 py-6 border-b border-[#27272A]">
        <Icon size={20} className="text-[#C5A059]" strokeWidth={1.5} />
        <h3 className="font-display text-xl uppercase font-medium">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-[#71717A] text-sm px-8 py-10">{emptyText}</p>
      ) : (
        rows.map((r, i) => (
          <div key={r.id} className="flex items-center gap-5 px-8 py-4 border-b border-[#27272A] last:border-0 hover:bg-[#18181B] transition-colors" data-testid={`${testid}-row-${i}`}>
            <span className={`font-display text-2xl font-bold w-8 ${i === 0 ? "text-[#C5A059]" : "text-[#3f3f46]"}`}>{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{r.prenom} {r.nom}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#71717A]">{r.poste_principal}{r.equipe ? ` · ${r.equipe}` : ""}</p>
            </div>
            <span className="font-display text-2xl text-[#C5A059] font-medium">{r.stats[field]}</span>
          </div>
        ))
      )}
    </div>
  );
}

export default function Leaderboard({ detectionId }) {
  const [data, setData] = useState({ buteurs: [], passeurs: [] });
  const { t } = useLang();

  useEffect(() => {
    const url = detectionId ? `${API}/leaderboard?detection_id=${detectionId}` : `${API}/leaderboard`;
    axios.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [detectionId]);

  return (
    <section id="classements" data-testid="leaderboard-section" className="py-28 lg:py-40 bg-[#121214]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8 mb-20"
        >
          <div>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("lb.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
              {t("lb.title.a")} <span className="text-[#C5A059]">{t("lb.title.b")}</span>
            </h2>
          </div>
          <p className="text-[#71717A] text-sm max-w-sm leading-relaxed">
            {t("lb.text")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid lg:grid-cols-2 gap-8"
        >
          <Board title={t("lb.scorers")} icon={Target} rows={data.buteurs} field="buts" testid="leaderboard-buteurs" emptyText={t("lb.empty")} />
          <Board title={t("lb.assists")} icon={Crosshair} rows={data.passeurs} field="passes" testid="leaderboard-passeurs" emptyText={t("lb.empty")} />
        </motion.div>
      </div>
    </section>
  );
}
